import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import type { Goal } from '@horizon/shared';
import { Progress } from '@/components/Progress';
import { GoalMeasure } from '@/components/GoalMeasure';

export function GoalCard({ goal }: { goal: Goal }) {
  const { t, i18n } = useTranslation();

  return (
    <li className="rounded-card border border-line bg-surface">
      <Link
        to={`/goals/${goal.id}`}
        className="flex flex-col gap-3 p-4 hover:border-accent"
        style={goal.color ? { borderInlineStartColor: goal.color } : undefined}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="flex min-w-0 items-baseline gap-2">
            {goal.emoji && <span aria-hidden="true">{goal.emoji}</span>}
            <span className="font-serif text-lg font-semibold">{goal.title}</span>
          </span>
          <span className="shrink-0 rounded-md bg-surface-2 px-2 py-0.5 text-xs text-muted">
            {t(`goal.horizon.${goal.horizon}`)}
          </span>
        </div>

        <Progress
          percent={goal.progressPercent}
          label={t('goal.progressOf', { title: goal.title })}
        />

        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
          <GoalMeasure goal={goal} />
          {goal.targetDate && (
            <span>{t('goal.until', { date: formatDate(goal.targetDate, i18n.language) })}</span>
          )}
          <span className={goal.pace === 'behind' ? 'text-near' : undefined}>
            {t(`goal.pace.${goal.pace}`)}
          </span>
        </p>

        {goal.nextAction && (
          <p className="flex items-center gap-2 text-sm text-ink-2">
            <ArrowRight aria-hidden="true" className="size-4 shrink-0 text-accent" />
            <span className="min-w-0 break-words">
              {t('goal.nextAction')}: {goal.nextAction.title}
            </span>
          </p>
        )}
      </Link>
    </li>
  );
}

function formatDate(date: string, locale: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}
